import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CompanySearch from '../CompanySearch';
import CompanyFormModal from '../CompanyFormModal';
import CompanyInfoTab from './CompanyInfoTab';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import PayrollGrid from './PayrollGrid';
import PayrollSettings from './PayrollSettings';
import WorkTypeCard, { BLANK_ROW, fillColors, fromSample, nextColor } from './WorkTypeCard';
import NetSolverPanel from './NetSolverPanel';
import RetireHealthPanel from './RetireHealthPanel';
import IdentityChangeDialog from './IdentityChangeDialog';
import EmployeeMemoPanel, { MEMO_CHANNEL } from './EmployeeMemoPanel';
import EmployeeLeavePanel from './EmployeeLeavePanel';
import { useDocumentPreview } from '../useDocumentPreview';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload, downloadFile } from '../../utils/api';
import { useDataFetch } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import config from '../../config';
import '../SalaryManagement.css';
import './Payroll.css';

const API = '/api/payroll';
const pad = (n) => String(n).padStart(2, '0');
const ymKey = (y, m) => `${y}-${pad(m)}`;

const MAX_UNDO = 100;

// 대장 화면의 탭. 상용직·일용직·사업소득은 표(시트)이고, 기업 정보·수당 설정·근무타입 설정은 그 달 대장의 바탕이다.
const TABS = [
  { id: 'company', label: '기업 정보' },
  { id: 'allowances', label: '수당 설정', settings: '계약서 수당' },
  { id: 'worktypes', label: '근무타입 설정', settings: '근무타입' },
  { id: 'fulltime', label: '상용직', sheet: true },
  { id: 'daily', label: '일용직', sheet: true },
  { id: 'business', label: '사업소득', sheet: true },
];
const SHEET_UNIT = { fulltime: '직원', daily: '일용직', business: '사업소득자' };

// 임금대장 화면에서 바로 추가하는 열. 계약서 시수·금액 항목은 수당 설정 탭의 시수연동·고정수당과 같은 것이다.
const ADD_COLUMNS = {
  wage: { list: 'wage_items', title: '계약서 시수 항목 추가', placeholder: '수당명 (예: 특근)', help: '계약서 시수·금액과 임금대장 지급에 열이 생깁니다. 빼거나 순서를 바꿀 때는 수당 설정 탭에서 합니다.' },
  fixed: { list: 'fixed_items', title: '계약서 금액 항목 추가', placeholder: '수당명 (예: 자격수당)', help: '계약서 금액과 임금대장 지급에 열이 생깁니다. 빼거나 순서를 바꿀 때는 수당 설정 탭에서 합니다.' },
  extra: { list: 'extra_items', title: '계약외 수당 열 추가', placeholder: '수당명 (예: 성과급)', help: '뺄 때는 열 머리에 마우스를 올려 ✕ 를 누르세요.' },
  ded: { list: 'deduction_items', title: '공제항목 열 추가', placeholder: '공제명 (예: 두루누리)', help: '뺄 때는 열 머리에 마우스를 올려 ✕ 를 누르세요.' },
};
const MULTIPLIERS = [0.5, 1, 1.5, 2];
const KINDS = ['기본', '통상', '비통상'];

// POST 로 받는 파일 내려받기 (utils/api 의 downloadFile 은 GET 전용)
const postDownload = async (endpoint, body, filename) => {
  const response = await fetch(`${config.BACKEND_URL}${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === 'string' ? data.detail : `내려받기 실패: ${response.status}`);
  }
  const url = window.URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const importNotice = (s) => {
  const parts = [`양식 업로드: 수정 ${s.updated_rows}명, 추가 ${s.created_rows}명, 바뀐 칸 ${s.changed_cells}개`];
  if (s.leaves && (s.leaves.added || s.leaves.updated || s.leaves.deleted)) {
    parts.push(`휴직 추가 ${s.leaves.added}건, 수정 ${s.leaves.updated}건, 삭제 ${s.leaves.deleted}건`);
  }
  if (s.unknown_columns) parts.push(`이 달 설정에 없는 열 ${s.unknown_columns}개는 건너뜀`);
  if (s.rejected.length) {
    parts.push(`반영 안 된 칸 ${s.rejected.length}개 — ${s.rejected.slice(0, 5).map((r) => `${r.cell} ${r.label}: ${r.reason}`).join(' / ')}${s.rejected.length > 5 ? ' …' : ''}`);
  }
  return parts.join(' · ');
};

// 변경 한 묶음을 한 줄로: "홍길동 · 결근 등 공제: (빈칸) → 3" / "24칸 변경 (홍길동 · 성명 등)"
const describeStep = (step, view) => {
  const first = step.cells[0];
  // 변경은 어느 시트의 것일 수도 있다 → 행이 있는 시트를 찾아 그 시트의 열에서 이름을 얻는다
  const sheets = [{ rows: view.rows, columns: view.columns }, ...Object.values(view.sheets || {})];
  const home = sheets.find((sh) => sh.rows.some((r) => r.id === first.row_id)) || sheets[0];
  const row = home.rows.find((r) => r.id === first.row_id);
  const col = home.columns.find((c) => c.key === first.key);
  const who = row ? (row.values.name || `${row.order}번`) : '삭제된 행';
  const show = (v) => {
    if (v == null || v === '') return '(빈칸)';
    const opt = col && col.options ? col.options.find((o) => o.value === v) : null;
    if (opt) return opt.label;
    if (Array.isArray(v)) return `${v.length}단계`;
    return typeof v === 'number' ? v.toLocaleString('ko-KR') : String(v);
  };
  if (step.cells.length === 1) return `${who} · ${col ? col.label : first.key}: ${show(first.before)} → ${show(first.after)}`;
  return `${step.cells.length}칸 변경 (${who} · ${col ? col.label : first.key} 등)`;
};

function MissingRates({ missing, isAdmin, onBack }) {
  const navigate = useNavigate();
  return (
    <div className="pr-blocked">
      {onBack && <button type="button" className="pr-btn pr-back" onClick={onBack} title="보던 화면으로 돌아갑니다">← 뒤로</button>}
      <div className="pr-blocked-icon">🔒</div>
      <h3>이 달에는 임금대장을 사용할 수 없습니다</h3>
      <p>해당 월에 유효한 아래 기준 데이터가 등록되어 있지 않습니다.</p>
      <ul>{missing.map((m) => <li key={m.item}>{m.label}</li>)}</ul>
      {isAdmin ? (
        <button type="button" className="btn btn-primary" onClick={() => navigate('/admin')}>관리자 메뉴 › 급여 기준값에서 등록하기</button>
      ) : (
        <p className="pr-muted">관리자에게 등록을 요청해 주세요.</p>
      )}
    </div>
  );
}

function CreatePanel({ year, month, sources, onCopy, onStartNew, onBack, busy }) {
  const [choice, setChoice] = useState(sources.length ? 0 : -1);
  const [copyRows, setCopyRows] = useState(true);
  return (
    <div className="pr-create">
      {onBack && <button type="button" className="pr-btn pr-back" onClick={onBack} title="보던 화면으로 돌아갑니다">← 뒤로</button>}
      <h3>{year}년 {month}월 임금대장이 아직 없습니다</h3>
      {sources.length > 0 && <p>인접한 달의 수당시수·고정수당·근무타입과 직원 계약 내용을 그대로 가져올 수 있습니다.</p>}
      <div className="pr-create-options">
        {sources.map((s, i) => (
          <label key={ymKey(s.year, s.month)} className={`pr-create-option ${choice === i ? 'on' : ''}`}>
            <input type="radio" name="pr-source" checked={choice === i} onChange={() => setChoice(i)} />
            <div>
              <strong>{s.label} ({s.year}년 {s.month}월)에서 가져오기</strong>
              <span>설정 전체 + 직원 {s.rows}명</span>
            </div>
          </label>
        ))}
        <label className={`pr-create-option ${choice === -1 ? 'on' : ''}`}>
          <input type="radio" name="pr-source" checked={choice === -1} onChange={() => setChoice(-1)} />
          <div>
            <strong>새로 시작</strong>
            <span>기본 제안형 수당시수·근무타입에서 추가·제거한 뒤 시작합니다</span>
          </div>
        </label>
      </div>
      {choice >= 0 && (
        <label className="pr-create-check">
          <input type="checkbox" checked={copyRows} onChange={(e) => setCopyRows(e.target.checked)} />
          직원과 계약 내용도 가져오기 (결근·시간공제·계약외 수당·기타 공제는 비우고, 근태 기간은 이 달 기준으로 다시 계산합니다)
        </label>
      )}
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy}
        onClick={() => (choice >= 0 ? onCopy(sources[choice], copyRows) : onStartNew())}
      >
        {busy ? '만드는 중…' : choice >= 0 ? '가져와서 만들기' : '설정하고 만들기'}
      </button>
    </div>
  );
}

function Ledger({ company, year, month, users, months, onMonthsChanged, onCompanyChanged, onOpenMonth, onOpenRow, focus, onFocusDone, headerSlot, tab, setTab }) {
  const { user } = useAuth();
  const isAdmin = user && user.level === '관리자';
  const base = `${API}/companies/${company.id}/ledgers/${year}/${month}`;
  // 대장이 없는 달·기준값이 없는 달에서 '뒤로': 기업 정보 화면으로 간다
  const goBack = useCallback(() => setTab('company'), [setTab]);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [settingsMode, setSettingsMode] = useState(null); // 'edit' | 'create'
  const [defaults, setDefaults] = useState(null);
  const [addCount, setAddCount] = useState(1);
  const selection = useRef(null);
  const pending = useRef(Promise.resolve());
  // 되돌리기·다시 실행: 한 번의 입력/붙여넣기/지우기가 한 묶음이다. 이 화면을 여는 동안만 유지한다.
  // 연달아 누를 때 최신 목록을 봐야 하므로 ref 가 원본이고, state 는 버튼 표시용 사본이다.
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [stacks, setStacks] = useState({ undo: [], redo: [] });
  const syncStacks = useCallback(() => setStacks({ undo: undoRef.current, redo: redoRef.current }), []);
  const [jump, setJump] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const fileInput = useRef(null);
  const [addColumn, setAddColumn] = useState(null);   // {kind: 'wage' | 'fixed' | 'extra' | 'ded', name, multiplier, item_kind, tax_free, tax_free_type}
  const [addWorkType, setAddWorkType] = useState(null);   // 바로 추가할 근무타입 {type, color, rows}
  const [dropping, setDropping] = useState(false);
  // 직원 id 가 있는 행의 성명·주민번호 변경은 서버가 저장하지 않고 돌려준다. 하나씩 범위를 물어 적용한다
  const [identityQueue, setIdentityQueue] = useState([]);
  const sheet = TABS.find((t) => t.id === tab && t.sheet) ? tab : 'fulltime';
  const settingsTab = TABS.find((t) => t.id === tab && t.settings) || null;

  // ---- 직원 메모: 달이 아니라 직원(직원 id)에 붙는다
  const [memos, setMemos] = useState({});              // {직원 id: {text, updated}} — 메모가 있는 직원만
  const [memoTarget, setMemoTarget] = useState(null);  // 오른쪽 패널에 연 직원 {rowId, employeeId, name, idNumber}
  const memoWin = useRef(null);                        // 따로 띄운 메모 창
  const memoChannel = useRef(null);
  const companyId = company.id;

  useEffect(() => {
    let alive = true;
    setMemos({});
    setMemoTarget(null);
    apiGet(`${API}/companies/${companyId}/memos`).then((r) => { if (alive) setMemos(r.memos || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [companyId]);

  // 저장 결과 반영: 메모 단추의 상태, 그리고 이번에 직원 id 가 붙은 행
  const applyMemoSaved = useCallback((saved) => {
    setMemos((prev) => {
      const next = { ...prev };
      if (saved.text) next[saved.employee_id] = { text: saved.text, updated: saved.updated };
      else delete next[saved.employee_id];
      return next;
    });
    const ids = new Set(saved.row_ids || []);
    if (!ids.size) return;
    const stamp = (rows) => rows.map((r) => (ids.has(r.id) ? { ...r, employee_id: saved.employee_id } : r));
    setView((v) => {
      if (!v || !v.rows) return v;
      const sheets = {};
      Object.entries(v.sheets || {}).forEach(([name, sh]) => { sheets[name] = { ...sh, rows: stamp(sh.rows) }; });
      return { ...v, rows: stamp(v.rows), sheets };
    });
    setMemoTarget((t) => (t && ids.has(t.rowId) ? { ...t, employeeId: saved.employee_id } : t));
  }, []);

  // 따로 띄운 메모 창에서 저장하면 이 화면에도 반영한다
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const ch = new BroadcastChannel(MEMO_CHANNEL);
    memoChannel.current = ch;
    ch.onmessage = (e) => {
      const msg = e.data || {};
      if (msg.type === 'saved' && msg.companyId === companyId) applyMemoSaved(msg.memo);
    };
    return () => { ch.close(); memoChannel.current = null; };
  }, [companyId, applyMemoSaved]);

  // ---- 직원 휴직: 메모처럼 직원에게 붙는 목록. 오른쪽 패널 자리를 메모와 함께 쓴다(둘 중 하나만 열린다)
  const [leaveRowId, setLeaveRowId] = useState(null);
  const openLeave = useCallback((row) => {
    setMemoTarget(null);
    setLeaveRowId((prev) => (prev === row.id ? null : row.id));
  }, []);

  const memoWindowOpen = () => Boolean(memoWin.current && !memoWin.current.closed);
  // 메모 단추: 메모 창을 따로 띄워 두었으면 그 창의 내용을 바꾸고, 아니면 오른쪽 패널을 연다
  const openMemo = useCallback((row) => {
    const target = { rowId: row.id, employeeId: row.employee_id || null, name: row.values.name || '', idNumber: row.values.id_number || '' };
    if (memoWindowOpen() && memoChannel.current) {
      memoChannel.current.postMessage({ type: 'open', companyId, companyName: company.name, target });
      memoWin.current.focus();
      return;
    }
    setLeaveRowId(null);
    // 이미 이 직원의 메모가 열려 있으면 닫는다
    setMemoTarget((prev) => (prev && prev.rowId === target.rowId ? null : target));
  }, [companyId, company.name]);
  const popOutMemo = () => {
    if (!memoTarget) return;
    const query = new URLSearchParams({ company: companyId, companyName: company.name, row: memoTarget.rowId, name: memoTarget.name, idNumber: memoTarget.idNumber });
    if (memoTarget.employeeId) query.set('employee', memoTarget.employeeId);
    memoWin.current = window.open(`/memo-window?${query}`, 'payroll-memo', 'width=420,height=700');
    if (memoWin.current) setMemoTarget(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');
    try {
      setView(await apiGet(base));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  // 화면을 비우지 않고 대장만 다시 받아 온다(휴직처럼 표 밖에서 바뀐 것이 계산에 들어갈 때)
  const refresh = useCallback(async () => {
    try {
      setView(await apiGet(base));
    } catch (e) {
      setNotice(e.message);
    }
  }, [base]);

  // 다른 화면(성명·주민번호 변경 창의 분포표)에서 이 달의 특정 줄을 찍어 들어온 경우: 그 시트를 열고 그 줄로 간다
  const loaded = Boolean(view && view.exists);
  useEffect(() => {
    if (!loaded || !focus || !focus.row) return;
    if (focus.sheet && TABS.some((t) => t.id === focus.sheet && t.sheet)) setTab(focus.sheet);
    setJump({ cells: [{ row_id: focus.row, key: 'name' }], nonce: Date.now() });
    if (onFocusDone) onFocusDone();
  }, [loaded, focus && focus.row]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeRows = useCallback((changed) => {
    setView((v) => {
      if (!v) return v;
      // 행마다 자기 시트(상용직은 rows, 나머지는 sheets[시트].rows)에 반영한다
      const merge = (rows, name) => {
        const mine = changed.filter((r) => (r.sheet || 'fulltime') === name);
        if (!mine.length) return rows;
        const byId = new Map(mine.map((r) => [r.id, r]));
        const next = rows.map((r) => byId.get(r.id) || r);
        mine.forEach((r) => { if (!rows.some((x) => x.id === r.id)) next.push(r); });
        return next;
      };
      const sheets = {};
      Object.entries(v.sheets || {}).forEach(([name, sh]) => { sheets[name] = { ...sh, rows: merge(sh.rows, name) }; });
      return { ...v, rows: merge(v.rows, 'fulltime'), sheets };
    });
  }, []);

  // 셀 수정은 보낸 순서대로 하나씩 처리한다(빠르게 연달아 입력해도 응답이 뒤바뀌지 않게)
  // mode: 'edit'(새 변경 → 되돌리기 목록에 쌓고 다시 실행 목록은 비움) | 'undo' | 'redo'
  const commit = useCallback((changes, mode = 'edit') => {
    pending.current = pending.current.then(async () => {
      setBusy(true);
      try {
        const res = await apiPut(`${base}/cells`, { changes });
        mergeRows(res.rows);
        if (res.identity_changes && res.identity_changes.length) setIdentityQueue((q) => [...q, ...res.identity_changes]);
        if (res.applied.length) {
          // 서버가 돌려준 '실제로 바뀐 셀'(before/after)이 반대 방향 동작의 재료가 된다
          const step = { cells: res.applied, at: new Date() };
          if (mode === 'undo') {
            redoRef.current = [...redoRef.current, step];
          } else {
            undoRef.current = [...undoRef.current, step].slice(-MAX_UNDO);
            if (mode === 'edit') redoRef.current = [];
          }
          syncStacks();
          if (mode !== 'edit') setJump({ cells: res.applied, nonce: Date.now() });
        }
        if (res.rejected.length) {
          const first = res.rejected[0];
          setNotice(`${res.rejected.length}개 셀은 반영되지 않았습니다 — ${first.reason}`);
        } else {
          setNotice('');
        }
      } catch (e) {
        setNotice(`저장 실패: ${e.message}`);
      } finally {
        setBusy(false);
      }
    });
    return pending.current;
  }, [base, mergeRows, syncStacks]);

  // 마지막 묶음의 셀들을 이전 값으로 다시 써 넣는다
  const replay = useCallback((ref, mode) => {
    const step = ref.current[ref.current.length - 1];
    if (!step) return;
    ref.current = ref.current.slice(0, -1);
    syncStacks();
    commit(step.cells.map((c) => ({ row_id: c.row_id, key: c.key, value: c.before })), mode);
  }, [commit, syncStacks]);
  const undo = useCallback(() => replay(undoRef, 'undo'), [replay]);
  const redo = useCallback(() => replay(redoRef, 'redo'), [replay]);

  const addRows = useCallback(async (count) => {
    try {
      const res = await apiPost(`${base}/rows`, { count, sheet });
      mergeRows(res.rows);
      onMonthsChanged();
      return res.rows;
    } catch (e) {
      setNotice(`직원 추가 실패: ${e.message}`);
      return null;
    }
  }, [base, sheet, mergeRows, onMonthsChanged]);

  // 지금 보고 있는 시트의 행과 열
  const sheetRows = !view || !view.exists ? [] : sheet === 'fulltime' ? view.rows : view.sheets[sheet].rows;
  const sheetColumns = !view || !view.exists ? [] : sheet === 'fulltime' ? view.columns : view.sheets[sheet].columns;

  const deleteSelectedRows = async () => {
    const range = selection.current;
    if (!range || !sheetRows.length) return;
    const targets = sheetRows.slice(range.r0, range.r1 + 1);
    const names = targets.map((r) => r.values.name || `${r.order}번`).join(', ');
    if (!window.confirm(`${targets.length}명을 이 달 대장에서 삭제할까요?\n${names}`)) return;
    try {
      setView(await apiPost(`${base}/rows/delete`, { row_ids: targets.map((r) => r.id) }));
      // 지운 행을 가리키는 변경은 되돌릴 수 없으므로 목록에서 뺀다
      const gone = new Set(targets.map((r) => r.id));
      const keep = (stack) => stack.filter((step) => !step.cells.some((c) => gone.has(c.row_id)));
      undoRef.current = keep(undoRef.current);
      redoRef.current = keep(redoRef.current);
      syncStacks();
      onMonthsChanged();
    } catch (e) {
      setNotice(`삭제 실패: ${e.message}`);
    }
  };

  // 계약외 수당·기타 공제항목은 이 화면에서 열로 추가·삭제한다(그 달 설정에 반영된다)
  const saveSettings = async (settings) => {
    setView(await apiPut(`${base}/settings`, { settings }));
  };

  const submitAddColumn = async () => {
    const name = addColumn.name.trim();
    if (!name) { setNotice('열 이름을 입력하세요'); return; }
    const { kind } = addColumn;
    const { list } = ADD_COLUMNS[kind];
    const taxFree = { tax_free: addColumn.tax_free, tax_free_type: addColumn.tax_free ? addColumn.tax_free_type : '', tax_free_limit: null };
    const item = {
      wage: { name, multiplier: Number(addColumn.multiplier), kind: addColumn.item_kind },
      fixed: { name, kind: addColumn.item_kind, ...taxFree },
      extra: { name, ...taxFree },
      ded: { name },
    }[kind];
    try {
      await saveSettings({ ...view.settings, [list]: [...view.settings[list], item] });
      setAddColumn(null);
      setNotice('');
      setJump({ cells: [], nonce: Date.now() });
    } catch (e) {
      setNotice(`열 추가 실패: ${e.message}`);
    }
  };

  // 묶음 띠의 추가 단추: 근무타입은 카드로, 나머지는 열 추가 줄로 연다
  const openAdd = (kind) => {
    setNotice('');
    if (kind === 'worktype') {
      setAddColumn(null);
      setAddWorkType({ type: '', color: nextColor(view.settings.work_types), rows: [{ ...BLANK_ROW }] });
      if (!defaults) apiGet(`${API}/defaults`).then(setDefaults).catch(() => {});      // 샘플 목록
      return;
    }
    setAddWorkType(null);
    setAddColumn({ kind, name: '', multiplier: 1, item_kind: kind === 'wage' ? '기본' : '통상', tax_free: false, tax_free_type: '' });
  };

  const submitAddWorkType = async () => {
    const type = addWorkType.type.trim();
    if (!type) { setNotice('근무타입 이름을 입력하세요'); return; }
    try {
      // 색이 없던 예전 타입에 먼저 색을 채워 보내야 새 타입의 색과 겹치지 않는다
      await saveSettings({ ...view.settings, work_types: [...fillColors(view.settings.work_types), { ...addWorkType, type }] });
      setAddWorkType(null);
      setNotice(`근무타입 '${type}'을 추가했습니다. 근무타입 칸에서 고를 수 있습니다`);
    } catch (e) {
      setNotice(`근무타입 추가 실패: ${e.message}`);
    }
  };

  const removeColumn = async (col) => {
    const [prefix, id] = col.key.split('.');
    const list = prefix === 'extra' ? 'extra_items' : 'deduction_items';
    const filled = view.rows.filter((r) => r.values[col.key] != null && r.values[col.key] !== '').length;
    const warn = filled ? `\n${filled}명에게 입력된 값이 계산에서 빠집니다.` : '';
    if (!window.confirm(`'${col.label}' 열을 ${year}년 ${month}월 대장에서 뺄까요?${warn}`)) return;
    try {
      await saveSettings({ ...view.settings, [list]: view.settings[list].filter((i) => i.id !== id) });
      // 없어진 열을 가리키는 되돌리기 항목은 뺀다
      const keep = (stack) => stack.filter((step) => !step.cells.some((c) => c.key === col.key));
      undoRef.current = keep(undoRef.current);
      redoRef.current = keep(redoRef.current);
      syncStacks();
    } catch (e) {
      setNotice(`열 삭제 실패: ${e.message}`);
    }
  };

  const downloadForm = async () => {
    try {
      const label = TABS.find((t) => t.id === sheet).label;
      await downloadFile(`${base}/form?sheet=${sheet}`, `${company.name}_${year}-${pad(month)}_${label}_양식.xlsx`);
    } catch (e) {
      setNotice(`양식 내려받기 실패: ${e.message}`);
    }
  };

  const uploadForm = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('sheet', sheet);
      const res = await apiUpload(`${base}/form`, fd);
      const s = res.import_summary;
      setView(res);
      if (res.identity_changes && res.identity_changes.length) setIdentityQueue(res.identity_changes);
      // 행이 새로 생기고 여러 칸이 한꺼번에 바뀌므로 이 화면의 되돌리기 목록은 비운다
      undoRef.current = [];
      redoRef.current = [];
      syncStacks();
      onMonthsChanged();
      setNotice(importNotice(s));
    } catch (e) {
      setNotice(`양식 업로드 실패: ${e.message}`);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const deleteLedger = async () => {
    if (!window.confirm(`${year}년 ${month}월 임금대장을 삭제할까요?`)) return;
    try {
      await apiDelete(base);
      onMonthsChanged();
      load();
    } catch (e) {
      setNotice(`삭제 실패: ${e.message}`);
    }
  };

  const create = async (body) => {
    setBusy(true);
    try {
      const created = await apiPost(base, body);
      setView(created);
      onMonthsChanged();
      if (created.skipped_left && created.skipped_left.length) {
        setNotice(`이미 퇴사한 ${created.skipped_left.length}명은 가져오지 않았습니다: ${created.skipped_left.map((s) => s.name).join(', ')}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const openCreateSettings = async () => {
    try {
      setDefaults(await apiGet(`${API}/defaults`));
      setSettingsMode('create');
    } catch (e) {
      setNotice(e.message);
    }
  };

  // 설정 탭은 근무타입 샘플이 필요하다
  useEffect(() => {
    if (!settingsTab || defaults) return;
    apiGet(`${API}/defaults`).then(setDefaults).catch((e) => setNotice(e.message));
  }, [settingsTab, defaults]);

  // 네트 역산은 직원 줄을 고르면 켜진다. 계약외 수당·공제 칸을 골랐으면 그 항목이 조정 항목으로 먼저 잡힌다
  const [netCell, setNetCell] = useState(null);      // {rowId, key}
  const [netOpen, setNetOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);   // 퇴사자 건강보험 정산 패널
  const [activeRowId, setActiveRowId] = useState(null);
  const onSelectionChange = useCallback((range, cell) => {
    selection.current = range;
    setActiveRowId(cell ? cell.rowId : null);
    const eligible = cell && cell.rowId && cell.key;
    setNetCell((prev) => {
      if (!eligible) return prev === null ? prev : null;
      return prev && prev.rowId === cell.rowId && prev.key === cell.key ? prev : cell;
    });
  }, []);

  const totals = useMemo(() => {
    if (!view || !view.exists) return null;
    const rows = sheet === 'fulltime' ? view.rows : view.sheets[sheet].rows;
    // 표의 합계 행과 같게, 화면에 보이는 정수(표시 보정값)를 더한다
    const shown = (r, key) => (r.recon && r.recon[key] ? r.recon[key].disp : Math.round(r.values[key]));
    const sum = (key) => rows.reduce((acc, r) => acc + (typeof r.values[key] === 'number' ? shown(r, key) : 0), 0);
    return { gross: sum(sheet === 'business' ? 'fee' : 'gross'), net: sum('net_pay') };
  }, [view, sheet]);

  // 문서(계약서·급여명세서·급상여대장)는 기존 임금대장의 미리보기 창과 생성 코드를 그대로 쓴다.
  // 각 행의 legacy 가 그 코드가 읽는 모양이고, 수당시수·근무타입은 서버가 이 달 대장 설정에서 읽는다.
  const legacyRows = useMemo(() => (view && view.exists ? view.rows.map((r) => r.legacy) : []), [view]);
  const activeRowIndex = view && view.exists ? view.rows.findIndex((r) => r.id === activeRowId) : -1;
  const preview = useDocumentPreview({
    company,
    currentYearMonth: { year, month },
    activeData: legacyRows,
    activeTab: 'fulltime',
    salaryData: { data: legacyRows, day: [], biz: [] },
    selectedRowIndex: activeRowIndex >= 0 ? activeRowIndex : null,
    apiBase: `${API}/docs`,
  });

  // 설정 창의 '설정 양식'(계약서 수당 + 근무타입) 내려받기·올리기. 올린 내용은 창에 채워질 뿐 저장되지 않는다.
  const settingsFormProps = {
    onDownloadForm: (settings) => postDownload(
      `${API}/settings-form`,
      {
        settings,
        title: `${company.name} ${year}년 ${month}월 임금대장 설정`,
        tax_free_types: ((view && view.tax_free_types) || []).map((t) => t.name),
      },
      `${company.name}_${year}-${pad(month)}_임금대장설정.xlsx`,
    ),
    onUploadForm: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiUpload(`${API}/settings-form/parse`, fd);
    },
  };

  if (loading) return <LoadingSpinner message="임금대장을 불러오는 중..." />;
  if (error) return <ErrorMessage message={error} type="error" />;
  if (!view) return null;

  const tabBar = (
    <div className="pr-tabs">
      {TABS.map((t) => {
        const count = !view.exists || !t.sheet ? null : t.id === 'fulltime' ? view.rows.length : view.sheets[t.id].rows.length;
        return (
          <button type="button" key={t.id} className={`pr-tab ${tab === t.id ? 'on' : ''}`} onClick={() => { setTab(t.id); setNotice(''); setNetOpen(false); setAddColumn(null); setAddWorkType(null); }}>
            {t.label}{count != null && <span className="pr-tab-count">{count}</span>}
          </button>
        );
      })}
    </div>
  );
  const companyTab = (
    <CompanyInfoTab
      company={company}
      users={users}
      months={months}
      current={{ year, month }}
      onChanged={onCompanyChanged}
      // 달을 누르면 그 달의 상용직 탭으로 간다
      onOpenMonth={(y, m) => { setTab('fulltime'); if (y !== year || m !== month) onOpenMonth(y, m); }}
    />
  );

  if (view.missing_rates.length) {
    return (
      <div className="pr-ledger">
        {tabBar}
        {tab === 'company' ? companyTab : <MissingRates missing={view.missing_rates} isAdmin={isAdmin} onBack={goBack} />}
      </div>
    );
  }

  if (!view.exists) {
    return (
      <div className="pr-ledger">
        {tabBar}
        {notice && <div className="pr-notice">{notice}</div>}
        {tab === 'company' ? companyTab : (
          <CreatePanel
            year={year}
            month={month}
            sources={view.sources}
            busy={busy}
            onCopy={(s, copyRows) => create({ copy_year: s.year, copy_month: s.month, copy_rows: copyRows }).catch((e) => setNotice(e.message))}
            onStartNew={openCreateSettings}
            onBack={goBack}
          />
        )}
        {settingsMode === 'create' && defaults && (
          <PayrollSettings
            title={`${year}년 ${month}월 임금대장 설정`}
            initial={defaults.settings}
            taxFreeItems={view.tax_free_types}
            workTypeSamples={defaults.work_type_samples}
            submitLabel="이 설정으로 만들기"
            onClose={() => setSettingsMode(null)}
            onSubmit={async (settings) => { await create({ settings }); setSettingsMode(null); }}
            {...settingsFormProps}
          />
        )}
      </div>
    );
  }

  if (tab === 'company') return <div className="pr-ledger">{tabBar}{companyTab}</div>;

  if (settingsTab) {
    return (
      <div className="pr-ledger">
        {tabBar}
        {notice && <div className="pr-notice" onClick={() => setNotice('')}>{notice}</div>}
        {!defaults ? <LoadingSpinner message="설정을 불러오는 중..." /> : (
          <PayrollSettings
            inline
            key={`${view.id}-${JSON.stringify(view.settings).length}-${view.settings.wage_items.length}`}
            section={tab}
            title={`${year}년 ${month}월 ${settingsTab.settings} (이 달에만 적용)`}
            initial={view.settings}
            taxFreeItems={view.tax_free_types}
            workTypeSamples={defaults.work_type_samples}
            submitLabel="저장"
            onSubmit={async (settings) => {
              setView(await apiPut(`${base}/settings`, { settings }));
              setNotice('설정을 저장했습니다');
            }}
            {...settingsFormProps}
          />
        )}
      </div>
    );
  }

  const isFulltime = sheet === 'fulltime';
  // 이 달 상용직에서 퇴사일이 적힌 직원 — 퇴사자 건강보험 정산 대상
  const retirees = view && view.exists ? view.rows.filter((r) => r.values.leave_date) : [];
  const unit = SHEET_UNIT[sheet];
  // 파일을 끌어다 놓으면 이 탭의 양식으로 올린다
  const hasFiles = (e) => Array.from(e.dataTransfer.types || []).includes('Files');
  const dropHandlers = {
    onDragOver: (e) => { if (hasFiles(e)) { e.preventDefault(); setDropping(true); } },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); },
    onDrop: (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDropping(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      if (!/\.xlsx$/i.test(file.name)) { setNotice('엑셀(.xlsx) 양식만 올릴 수 있습니다'); return; }
      uploadForm(file);
    },
  };

  // 네트 역산을 할 수 있는 칸: 금액을 움직일 수 있는 항목(계약외 수당·공제)과 맞출 대상인 차인지급액
  const netColumn = view.columns.find((c) => c.key === 'net_steps');
  const netReady = Boolean(netCell && netColumn
    && (netCell.key === 'net_pay' || netColumn.options.some((o) => o.value === netCell.key)));
  const sheetActions = (
    <>
      <span className="prg-bar-title">편집</span>
      <button type="button" className="pr-btn" onClick={undo} disabled={!stacks.undo.length} title={stacks.undo.length ? `되돌리기 (Ctrl+Z): ${describeStep(stacks.undo[stacks.undo.length - 1], view)}` : '되돌릴 변경이 없습니다'}>↶ 되돌리기</button>
      <button type="button" className="pr-btn" onClick={redo} disabled={!stacks.redo.length} title={stacks.redo.length ? `다시 실행 (Ctrl+Y): ${describeStep(stacks.redo[stacks.redo.length - 1], view)}` : '다시 실행할 변경이 없습니다'}>↷ 다시 실행</button>
      <div className="pr-history-wrap">
        <button type="button" className="pr-btn" onClick={() => setShowHistory((v) => !v)} disabled={!stacks.undo.length}>변경 이력 {stacks.undo.length || ''}</button>
        {showHistory && stacks.undo.length > 0 && (
          <div className="pr-history">
            <div className="pr-history-head">이 화면에서 한 변경 (최근 순) · 누르면 그 셀로 이동</div>
            {[...stacks.undo].reverse().map((step, i) => (
              <button type="button" className="pr-history-item" key={`${step.at.getTime()}-${i}`} onClick={() => { setJump({ cells: step.cells, nonce: Date.now() }); setShowHistory(false); }}>
                <span className="pr-history-time">{step.at.toTimeString().slice(0, 8)}</span>
                <span className="pr-history-text">{describeStep(step, view)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <span className="pr-toolbar-sep" />
      <input
        type="number"
        min="1"
        max="500"
        className="pr-add-count"
        value={addCount}
        onChange={(e) => setAddCount(e.target.value)}
      />
      <button type="button" className="pr-btn" onClick={() => addRows(Math.max(1, Number(addCount) || 1))}>+ 직원 추가</button>
      <button type="button" className="pr-btn" onClick={deleteSelectedRows} disabled={!sheetRows.length}>선택 행 삭제</button>
      {isFulltime && (
        <>
          <span className="pr-toolbar-sep" />
          <button
            type="button"
            className="pr-btn rainbow"
            disabled={!netReady}
            onClick={() => setNetOpen(true)}
            title={netReady ? '계약외 수당이나 공제 항목의 금액을 움직여 차인지급액을 목표액에 맞춥니다. 단계를 쌓을 수 있고, 적용 전에 예상 변화를 먼저 보여 줍니다.' : '직원의 계약외 수당·공제 항목 칸이나 차인지급액 칸을 고르면 켜집니다'}
          >
            네트 역산
          </button>
          {retirees.length > 0 && (
            <button
              type="button"
              className="pr-btn"
              onClick={() => setRetireOpen(true)}
              title="퇴사자 한 명씩, 퇴직한 해(1~3월 퇴직이면 전년도까지)의 건강보험료·장기요양보험료를 다시 계산해 그동안 뗀 보험료와의 차액을 보여 줍니다. [추가하기]를 누르면 공제항목 「건강보험 퇴직정산」에 넣습니다."
            >
              퇴사자 건강보험 정산 <span className="pr-btn-count">{retirees.length}</span>
            </button>
          )}
        </>
      )}
      <span className="pr-toolbar-info">
        {view.copied_from && `${view.copied_from.year}년 ${view.copied_from.month}월에서 가져옴 · `}
        {unit} {sheetRows.length}명
        {totals && ` · 세전 ${Math.round(totals.gross).toLocaleString('ko-KR')} · 공제 ${Math.round(totals.gross - totals.net).toLocaleString('ko-KR')} · 차인 ${Math.round(totals.net).toLocaleString('ko-KR')}`}
        <span className="pr-saving">{busy ? ' · 저장 중…' : ''}</span>
      </span>
      {isFulltime && (
        <span className="pr-docs">
          <span className="prg-bar-title">서류</span>
          <button type="button" className="pr-btn" onClick={preview.handleOpenContractPreview} disabled={activeRowIndex < 0} title={activeRowIndex < 0 ? '직원 줄을 고르면 계약서를 볼 수 있습니다' : '고른 직원의 계약서 미리보기 (새 창)'}>계약서</button>
          <button type="button" className="pr-btn" onClick={preview.handleOpenPayslipPreview} disabled={!view.rows.length} title="급여명세서 미리보기 (새 창). 고른 직원부터 보여 주고 창 안에서 앞뒤로 넘깁니다">급여명세서</button>
          <button type="button" className="pr-btn" onClick={preview.handleOpenTriplePreview} disabled={!view.rows.length} title="급상여대장 미리보기 (새 창)">급상여대장</button>
        </span>
      )}
    </>
  );

  return (
    <div className={`pr-ledger ${dropping ? 'dropping' : ''}`} {...dropHandlers}>
      {tabBar}
      {dropping && <div className="pr-drop">여기에 놓으면 {TABS.find((t) => t.id === sheet).label} 양식을 올립니다<span>행ID 가 있는 줄은 수정, 없는 줄은 새로 추가됩니다</span></div>}
      {headerSlot && createPortal(
        <>
          <button type="button" className="pr-btn" onClick={downloadForm} title={isFulltime ? '인적정보·계약서 내용을 넣는 수식 없는 엑셀 양식입니다. 지금 입력된 값이 채워져 있습니다. 근태·계약외 수당·공제는 이 화면에서 입력합니다.' : '이 탭의 입력 열을 담은 수식 없는 엑셀 양식입니다. 지금 입력된 값이 채워져 있습니다.'}>⬇ 양식 내려받기</button>
          <button type="button" className="pr-btn" onClick={() => fileInput.current && fileInput.current.click()} title="내려받은 양식을 채워 올립니다(파일을 표 위에 끌어다 놓아도 됩니다). 행ID 가 있는 행은 수정, 없는 행은 새로 추가됩니다.">⬆ 양식 업로드</button>
          <button type="button" className="pr-btn danger" onClick={deleteLedger}>대장 삭제</button>
        </>,
        headerSlot,
      )}
      <input ref={fileInput} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={(e) => uploadForm(e.target.files[0])} />
      {addColumn && (() => {
        const def = ADD_COLUMNS[addColumn.kind];
        const taxable = addColumn.kind === 'fixed' || addColumn.kind === 'extra';
        return (
          <div className="pr-addcol">
            <b>{def.title}</b>
            <input
              autoFocus
              value={addColumn.name}
              placeholder={def.placeholder}
              onChange={(e) => setAddColumn({ ...addColumn, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') submitAddColumn(); if (e.key === 'Escape') setAddColumn(null); }}
            />
            {addColumn.kind === 'wage' && (
              <select value={addColumn.multiplier} onChange={(e) => setAddColumn({ ...addColumn, multiplier: e.target.value })} title="통상시급 × 시수 × 배수로 계산합니다">
                {MULTIPLIERS.map((m) => <option key={m} value={m}>배수 ×{m}</option>)}
              </select>
            )}
            {(addColumn.kind === 'wage' || addColumn.kind === 'fixed') && (
              <select value={addColumn.item_kind} onChange={(e) => setAddColumn({ ...addColumn, item_kind: e.target.value })} title="구분">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            )}
            {taxable && (
              <>
                <select value={addColumn.tax_free ? 'free' : 'tax'} onChange={(e) => setAddColumn({ ...addColumn, tax_free: e.target.value === 'free', tax_free_type: '' })}>
                  <option value="tax">과세</option>
                  <option value="free">비과세</option>
                </select>
                {addColumn.tax_free && (
                  <select value={addColumn.tax_free_type} onChange={(e) => setAddColumn({ ...addColumn, tax_free_type: e.target.value })}>
                    <option value="">비과세 유형 선택…</option>
                    {view.tax_free_types
                      .filter((t) => ![...view.settings.fixed_items, ...view.settings.extra_items].some((i) => i.tax_free && i.tax_free_type === t.name))
                      .map((t) => <option key={t.name} value={t.name}>{t.name}{t.monthly_limit != null ? ` (월 ${t.monthly_limit.toLocaleString('ko-KR')})` : ''}</option>)}
                  </select>
                )}
              </>
            )}
            <button type="button" className="pr-btn primary" onClick={submitAddColumn}>추가</button>
            <button type="button" className="pr-btn" onClick={() => setAddColumn(null)}>취소</button>
            <span className="pr-muted">이 달 대장에만 추가됩니다. {def.help}</span>
          </div>
        );
      })()}
      {addWorkType && (
        <div className="pr-addwt">
          <div className="pr-addwt-head">
            <b>근무타입 추가</b>
            {defaults && defaults.work_type_samples.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  const sample = defaults.work_type_samples.find((x) => x.type === e.target.value);
                  if (sample) setAddWorkType({ ...fromSample(sample, view.settings.work_types), color: addWorkType.color });
                }}
              >
                <option value="">샘플로 채우기…</option>
                {defaults.work_type_samples.map((x) => <option key={x.type} value={x.type}>{x.type} ({x.rows.map((r) => r.days).join(' / ')})</option>)}
              </select>
            )}
            <button type="button" className="pr-btn primary" onClick={submitAddWorkType}>추가</button>
            <button type="button" className="pr-btn" onClick={() => setAddWorkType(null)}>취소</button>
            <span className="pr-muted">이 달 대장에만 추가됩니다. 고치거나 뺄 때는 근무타입 설정 탭에서 합니다.</span>
          </div>
          <WorkTypeCard
            autoFocus
            wt={addWorkType}
            taken={new Set(fillColors(view.settings.work_types).map((w) => w.color))}
            onChange={(changes) => setAddWorkType({ ...addWorkType, ...changes })}
          />
        </div>
      )}
      {identityQueue.length > 0 && (
        <IdentityChangeDialog
          key={`${identityQueue[0].row_id}-${identityQueue.length}`}
          companyId={companyId}
          change={identityQueue[0]}
          remaining={identityQueue.length - 1}
          onDone={(res) => {
            setIdentityQueue((q) => q.slice(1));
            if (!res) return;
            mergeRows(res.rows);
            setNotice(`성명·주민번호 변경: ${res.months.length}개 달 ${res.changed_rows}개 줄에 적용했습니다${res.master_changed.length ? '' : ' (직원 정보의 현재값은 그대로입니다)'}`);
          }}
          onGo={(place) => {
            setIdentityQueue([]);
            if (place.year === year && place.month === month) {
              setTab(place.sheet);
              setJump({ cells: [{ row_id: place.row_id, key: 'name' }], nonce: Date.now() });
            } else if (onOpenRow) {
              onOpenRow(place);
            }
          }}
        />
      )}
      {/* 알림은 표 위에 띄워서(아래쪽) 표가 밀리지 않게 한다 */}
      {notice && <div className="pr-notice pr-toast" onClick={() => setNotice('')} title="눌러서 닫기">{notice}</div>}
      {netOpen && netReady && (() => {
        const row = view.rows.find((r) => r.id === netCell.rowId);
        const stepsColumn = view.columns.find((c) => c.key === 'net_steps');
        if (!row || !stepsColumn) return null;
        const apply = (steps) => {
          const cells = [{ row_id: row.id, key: 'net_steps', value: steps.length ? steps : null }];
          // 단계가 생기기 전 방식으로 저장된 값은 함께 지운다(안 지우면 단계를 다 없애도 되살아난다)
          ['net_amount', 'net_item'].forEach((key) => { if (row.values[key] != null) cells.push({ row_id: row.id, key, value: null }); });
          commit(cells);
          setNetOpen(false);
        };
        return (
          <NetSolverPanel
            key={row.id}
            base={base}
            row={row}
            options={stepsColumn.options}
            initialKey={netCell.key}
            onApply={apply}
            onClose={() => setNetOpen(false)}
          />
        );
      })()}
      {retireOpen && isFulltime && retirees.length > 0 && (
        <RetireHealthPanel
          base={base}
          retirees={retirees}
          initialRowId={activeRowId}
          onClose={() => setRetireOpen(false)}
          onApplied={(res) => {
            // 공제항목(열)이 새로 생길 수 있어 화면 전체를 받은 것으로 바꾼다
            setView(res);
            setRetireOpen(false);
            const item = res.retire_health.item;
            setNotice(`「${item.name}」에 ${Math.round(item.amount).toLocaleString('ko-KR')}원을 넣었습니다 — ${res.retire_health.employee.name}`);
            setJump({ cells: [{ row_id: res.retire_health.employee.row_id, key: item.key }], nonce: Date.now() });
          }}
        />
      )}
      <div className="pr-grid-area">
        <PayrollGrid
          key={sheet}
          columns={sheetColumns}
          rows={sheetRows}
          busy={false}
          onCommit={commit}
          onAddRows={addRows}
          onSelectionChange={onSelectionChange}
          onUndo={undo}
          onRedo={redo}
          onRemoveColumn={isFulltime ? removeColumn : undefined}
          onAddColumn={isFulltime ? openAdd : undefined}
          jump={jump}
          toolbar={sheetActions}
          memos={memos}
          onOpenMemo={openMemo}
          memoRowId={memoTarget ? memoTarget.rowId : null}
          onOpenLeave={isFulltime ? openLeave : undefined}
          leaveRowId={leaveRowId}
          empty={(
            <div className="pr-empty-card">
              <h3>아직 {unit}{sheet === 'business' ? '가' : '이'} 없습니다</h3>
              <p>몇 명을 추가하시겠습니까?</p>
              <div className="pr-empty-add">
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={addCount}
                  onChange={(e) => setAddCount(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addRows(Math.max(1, Number(addCount) || 1)); e.stopPropagation(); }}
                />
                <span>명</span>
                <button type="button" className="pr-btn primary" onClick={() => addRows(Math.max(1, Number(addCount) || 1))}>+ 추가</button>
              </div>
              <p className="pr-muted">
                엑셀로 한꺼번에 넣으려면 <button type="button" className="pr-link" onClick={downloadForm}>양식 내려받기</button>로 받은 파일을 채워
                {' '}<button type="button" className="pr-link" onClick={() => fileInput.current && fileInput.current.click()}>양식 업로드</button>하거나,
                이 화면에 파일을 끌어다 놓으세요.
              </p>
            </div>
          )}
        />
      {isFulltime && leaveRowId && view.rows.some((r) => r.id === leaveRowId) && (
        <EmployeeLeavePanel
          key={leaveRowId}
          companyId={companyId}
          row={view.rows.find((r) => r.id === leaveRowId)}
          year={year}
          month={month}
          onChanged={refresh}
          onCommit={commit}
          onClose={() => setLeaveRowId(null)}
        />
      )}
      {memoTarget && (
        <EmployeeMemoPanel
          key={memoTarget.rowId}
          companyId={companyId}
          target={memoTarget}
          onSaved={applyMemoSaved}
          onClose={() => setMemoTarget(null)}
          onPopout={popOutMemo}
        />
      )}
      </div>
    </div>
  );
}

function Payroll() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [sidebarFolded, setSidebarFolded] = useState(false);
  const [months, setMonths] = useState({});
  const [headerSlot, setHeaderSlot] = useState(null);   // 달 고르는 줄 오른쪽: 대장 화면이 양식·삭제 단추를 여기에 넣는다

  const { data: companies, loading: companiesLoading, error: companiesError, refetch: refetchCompanies } =
    useDataFetch(() => apiGet('/api/companies/'), []);
  const { data: users } = useDataFetch(() => apiGet('/api/companies/users/list'), []);

  const now = new Date();
  const year = Number(searchParams.get('year')) || now.getFullYear();
  const month = Number(searchParams.get('month')) || now.getMonth() + 1;
  const companyId = searchParams.get('company');
  const company = useMemo(
    () => (companies || []).find((c) => String(c.id) === String(companyId)) || null,
    [companies, companyId],
  );

  const setParams = (changes, replace = false) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, String(v))));
    setSearchParams(next, { replace });
  };

  // 고른 탭은 달을 옮겨도 그대로 두고(대장 화면은 달마다 새로 열리므로 여기서 들고 있는다), 주소에도 싣는다(?tab=…).
  // 같은 클릭에서 기업·달과 함께 바뀌는 일이 많아, 탭은 상태로 들고 있다가 바뀐 뒤에 주소에 맞춘다.
  // 주소가 먼저 바뀌면(뒤로 가기, 받은 주소 열기) 탭이 주소를 따라간다. 기업을 고르면 기업 정보부터 보여 준다
  const urlTab = searchParams.get('tab') || 'fulltime';
  const [tab, setTab] = useState(urlTab);
  useEffect(() => { setTab(urlTab); }, [urlTab]);
  useEffect(() => {
    if (tab !== urlTab) setParams({ tab: tab === 'fulltime' ? null : tab }, true);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMonths = useCallback(async () => {
    if (!company) { setMonths({}); return; }
    try {
      setMonths(await apiGet(`${API}/companies/${company.id}/months`));
    } catch (e) {
      setMonths({});
    }
  }, [company]);

  useEffect(() => { loadMonths(); }, [loadMonths]);

  if (companiesLoading) return <div className="salary-management"><LoadingSpinner message="데이터를 불러오는 중..." /></div>;
  if (companiesError) return <div className="salary-management"><ErrorMessage message={companiesError} type="error" /></div>;

  return (
    <div className="salary-management">
      <div className="salary-layout">
        <aside className={`company-sidebar ${sidebarFolded ? 'folded' : ''}`}>
          <div className="sidebar-content">
            <CompanySearch
              companies={companies || []}
              onCompanySelect={(c) => { if (c && String(c.id) !== String(companyId)) setTab('company'); setParams({ company: c ? c.id : null }); }}
              selectedCompany={company}
              onAddCompany={() => setShowCompanyModal(true)}
              onEditCompany={(c) => { if (c) { refetchCompanies(); setParams({ company: c.id }); } }}
              users={users || []}
            />
          </div>
          <button
            type="button"
            className="sidebar-fold-toggle"
            onClick={() => setSidebarFolded(!sidebarFolded)}
            title={sidebarFolded ? '기업 검색 펼치기' : '기업 검색 접기'}
          >
            <span className="sidebar-fold-icon">{sidebarFolded ? '›' : '‹'}</span>
          </button>
          {sidebarFolded && <span className="sidebar-fold-label">기업검색</span>}
        </aside>

        <main className="salary-content pr-main">
          {company ? (
            <>
              <div className="pr-header">
                <h2>{company.name}</h2>
                <div className="pr-months">
                  <button type="button" className="pr-year-btn" onClick={() => setParams({ year: year - 1, month })}>‹</button>
                  <span className="pr-year">{year}년</span>
                  <button type="button" className="pr-year-btn" onClick={() => setParams({ year: year + 1, month })}>›</button>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                    const info = months[ymKey(year, m)];
                    return (
                      <button
                        type="button"
                        key={m}
                        className={`pr-month ${m === month ? 'on' : ''} ${info ? 'has' : ''}`}
                        title={info ? `직원 ${info.rows}명` : '대장 없음'}
                        onClick={() => setParams({ year, month: m })}
                      >
                        {m}월
                      </button>
                    );
                  })}
                  <span className="pr-month-nav">
                    <button type="button" className="pr-year-btn" title="전월로 이동" onClick={() => setParams(month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 })}>◀ 전월</button>
                    <button type="button" className="pr-year-btn" title="다음 달로 이동" onClick={() => setParams(month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 })}>다음 달 ▶</button>
                  </span>
                </div>
                <div className="pr-header-actions" ref={setHeaderSlot} />
              </div>
              <Ledger
                headerSlot={headerSlot}
                tab={tab}
                setTab={setTab}
                key={`${company.id}-${year}-${month}`}
                company={company}
                year={year}
                month={month}
                users={users || []}
                months={months}
                onMonthsChanged={loadMonths}
                onOpenMonth={(y, m) => setParams({ year: y, month: m })}
                // 다른 달의 특정 줄로 가기: 달을 바꾸면서 줄·시트를 주소에 싣고, 대장이 열리면 지운다
                onOpenRow={(place) => setParams({ year: place.year, month: place.month, row: place.row_id, sheet: place.sheet })}
                focus={{ row: searchParams.get('row'), sheet: searchParams.get('sheet') }}
                onFocusDone={() => setParams({ row: null, sheet: null })}
                onCompanyChanged={(c) => { refetchCompanies(); if (c) setParams({ company: c.id }); }}
              />
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-content">
                <div className="no-selection-icon">🧮</div>
                <h3>임금대장</h3>
                <p>왼쪽에서 기업을 선택하거나 신규 기업을 등록해주세요</p>
              </div>
            </div>
          )}
        </main>
      </div>

      <CompanyFormModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSuccess={(c) => { refetchCompanies(); setParams({ company: c.id }); setShowCompanyModal(false); }}
        users={users || []}
      />
    </div>
  );
}

export default Payroll;
